import React, { useEffect, useRef, useState } from "react";
import { business } from "../data/business";
import { findDish, fullName } from "../data/dishes";
import { galleryPhotos, socialPhotos } from "../data/photos";
import { useSite } from "../hooks/useSite";

function BaseDialog({ open, onClose, label, className = "", children }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      document.body.classList.add("ro-locked");
    } else if (!open && el.open) el.close();
  }, [open]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const handleClose = () => {
      document.body.classList.remove("ro-locked");
      onClose();
    };
    const handleClick = (e) => {
      if (e.target === el) el.close();
    };
    el.addEventListener("close", handleClose);
    el.addEventListener("click", handleClick);
    return () => {
      el.removeEventListener("close", handleClose);
      el.removeEventListener("click", handleClick);
    };
  }, [onClose]);
  return (
    <dialog ref={ref} className={`ro-modal ${className}`} aria-label={label}>
      <div className="ro-modal__inner">
        <button type="button" className="ro-modal__close" aria-label="Close dialog" onClick={() => ref.current.close()}>
          ×
        </button>
        {children}
      </div>
    </dialog>
  );
}

// Provider chooser: hands off to Swiggy/Zomato. Nothing is added to a cart here.
export function ProviderDialog() {
  const { modal, closeModal } = useSite();
  const open = modal && modal.type === "order";
  const dish = open && modal.dishId ? findDish(modal.dishId) : null;
  const itemName = dish ? fullName(dish) : open && modal.itemName ? modal.itemName : null;
  return (
    <BaseDialog open={!!open} onClose={closeModal} label="Order online">
      <h2>
        Where’s your next <span className="ro-outline">RollOver?</span>
      </h2>
      <p>
        {itemName
          ? `Looking for ${itemName}? These links open the outlet’s live menu, where the current listing and price apply.`
          : "Choose a delivery listing to see the live menu, prices and delivery area."}
      </p>
      {business.providers.map((p) => (
        <a key={p.id} className="ro-provider" href={p.url} target="_blank" rel="noopener noreferrer">
          <span>
            {p.name}
            <small>{p.note}</small>
          </span>
          <span aria-hidden="true">↗</span>
        </a>
      ))}
      <p className="ro-small" style={{ marginTop: 24 }}>
        Opens in a new tab.
      </p>
    </BaseDialog>
  );
}

// Lightbox gallery of real RollOver photographs.
export function GalleryDialog() {
  const { modal, closeModal } = useSite();
  const open = modal && modal.type === "gallery";
  const photos = open && modal.photos ? modal.photos : open && modal.source === "social" ? socialPhotos : galleryPhotos;
  const title = (open && modal.title) || "Good-mood";
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (open) setIndex(modal.index || 0);
  }, [open, modal]);
  const count = photos.length;
  const go = (step) => setIndex((i) => (i + step + count) % count);
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") go(-1);
    if (e.key === "ArrowRight") go(1);
  };
  const photo = photos[index] || photos[0];
  return (
    <BaseDialog open={!!open} onClose={closeModal} label="Photo gallery" className="ro-modal--gallery">
      <div onKeyDown={onKeyDown}>
        <h2>
          {title} <span className="ro-outline">gallery.</span>
        </h2>
        <img className="ro-gallery__image" src={photo.src} alt={photo.alt} />
        <div className="ro-carousel__controls">
          <button type="button" className="ro-round" onClick={() => go(-1)} aria-label="Previous photograph">
            ←
          </button>
          <span className="ro-counter" aria-live="polite">
            {index + 1} / {count}
          </span>
          <button type="button" className="ro-round" onClick={() => go(1)} aria-label="Next photograph">
            →
          </button>
        </div>
        <p className="ro-carousel__caption">{photo.alt} · RollOver photography</p>
      </div>
    </BaseDialog>
  );
}
